import { Link } from "react-router-dom";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRotateRight } from "@fortawesome/free-solid-svg-icons";
import Button from "../components/atoms/Button";
import Spinner from "../components/atoms/Spinner";
import { v4 as uuidv4 } from "uuid";
import OutageAlert from "../components/atoms/OutageAlert";
import RetailCard from "./Retail/RetailCard";
import ConcessionCard from "./Concession/ConcessionCard";
import FacilitiesCard from "./Facilities/FacilitiesCard";
import GatesCard from "./Gates/GatesCard";
import LocationsHeader from "./LocationsHeader";
import LocationsController from "./LocationsController";

export default function Locations() {
  return (
    <LocationsController>
      {({
        isLoading,
        retail,
        concessions,
        facilities,
        gates,
        retailLocationData,
        concessionLocationData,
        facilitiesLocationData,
        gatesLocationData,
        showOutageAlert,
        showFoodSection,
        showBeverageSection,
        showRetailSection,
        showFacilitiesSection,
        showGatesSection,
        foodItems,
        beverageItems,
        authCtx,
        handleSearch,
        handleSort,
        handleDismiss,
        refreshLocations,
        lastUpdatedAt,
        nextRefreshAt,
        isRefreshing,
        refreshNow
      }) => {
        const isSessionValid = authCtx.loggedIn &&
          moment.unix(authCtx.tokenExpirationTime) > moment(new Date());

        if (!isSessionValid) {
          return (
            <Link to="/auth">
              {authCtx.loggedIn ?
                "Session Token expired or invalid. Please log out, then log back in for a new session token" :
                "Not Authorized. Please Log-In with given credentials or contact support."
              }
            </Link>
          );
        }

        if (isLoading) {
          return (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100vw',
              height: '100vh'
            }}>
              <Spinner size="large" />
            </div>
          );
        }

        return (
          <div className="relative min-h-screen">
            {authCtx.loggedIn && moment.unix(authCtx.tokenExpirationTime) > moment(new Date()) && (
              <LocationsHeader
                onSearch={handleSearch}
                onSort={handleSort}
                refreshStatus={{ lastUpdatedAt, nextRefreshAt, isRefreshing, onRefresh: refreshNow }}
              />
            )}
            <div className="p-4">

              {showRetailSection && (
                <>
                  {/* Retail label and Refresh button in one row */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '16px'
                    }}
                  >
                    <div style={{ fontSize: "1.5rem" }}>Retail</div>
                    <Button
                      className="border-0"
                      onClick={refreshLocations}
                      disabled={isLoading}
                      style={{ marginRight: '8px' }}
                    >
                      <FontAwesomeIcon icon={faArrowRotateRight} />
                      <span style={{ marginLeft: '8px' }}>Refresh</span>
                    </Button>
                  </div>

                  {/* Regular locations */}
                  <div className="rx-card-grid">
                    {retail
                      .filter(location => location.maxOccupancy !== 0)
                      .map((location) => (
                        <RetailCard
                          key={location.id}
                          locationData={location}
                          data={retailLocationData[location.id] || { current: 0, percentage: 0 }}
                        />
                      ))}
                  </div>

                  {/* Locations with maxOccupancy 0 */}
                  {retail.some(location => location.maxOccupancy === 0) && (
                    <div className="rx-card-grid mt-4">
                      {retail
                        .filter(location => location.maxOccupancy === 0)
                        .map((location) => (
                          <RetailCard
                            key={location.id}
                            locationData={location}
                            data={retailLocationData[location.id] || { count: 0 }}
                          />
                        ))}
                    </div>
                  )}
                </>
              )}

              {/* F&B Section */}
              {showFoodSection && (
                <div className="row" style={{ display: 'flex', flexDirection: 'column' }}>
                  {showRetailSection && <hr style={{ marginTop: '24px' }} />}
                  <div style={{ paddingLeft: '16px' }}>
                    <div style={{ fontSize: "1.5rem", marginBottom: '16px' }}>
                      F&B
                    </div>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                      gap: '24px'
                    }}>
                      {foodItems.map((concession) => (
                        <ConcessionCard
                          key={concession.id}
                          locationData={concession}
                          data={concessionLocationData[concession.id]}
                        />
                      ))}
                    </div>
                  </div>
                  
                  {/* {showBeverageSection && (
                    <div style={{ paddingLeft: '16px' }}>
                      <div style={{
                        fontSize: "1.5rem",
                        marginBottom: '16px',
                        marginTop: showFoodSection ? '32px' : '0'
                      }}>
                        Drinks
                      </div>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                        gap: '24px'
                      }}>
                        {beverageItems.map((concession) => (
                          <ConcessionCard
                            key={concession.name}
                            {...concession}
                          />
                        ))}
                      </div>
                    </div>
                  )} */}
                </div>
              )}

              {/* Gates Section */}
              {showGatesSection && (
                <div className="row" style={{ display: 'flex', flexDirection: 'row' }}>
                  {(showRetailSection || showFoodSection) && <hr style={{ marginTop: '24px' }} />}

                  <div style={{ paddingLeft: '16px' }}>
                    <div style={{
                      fontSize: "1.5rem",
                      marginBottom: '16px',
                    }}>
                      Gates
                    </div>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                      gap: '24px'
                    }}>
                      {gates.map((location) => (
                        <GatesCard
                          key={location.id}
                          locationData={location}
                          data={gatesLocationData[location.id]}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {showFacilitiesSection && (
                <div className="row" style={{ display: 'flex', flexDirection: 'row' }}>
                  {(showRetailSection || showFoodSection || showGatesSection) && <hr style={{ marginTop: '24px' }} />}

                   {/* Facilities Section */}
                   <div style={{ paddingLeft: '16px' }}>
                    <div style={{
                      fontSize: "1.5rem",
                      marginBottom: '16px',
                      // marginTop: (showFoodSection || showBeverageSection) ? '32px' : '0'
                    }}>
                      Facilities
                    </div>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                      gap: '24px'
                    }}>
                    {facilities
                      .map((location) => (
                        <FacilitiesCard
                          key={location.id}
                          locationData={location}
                          data={facilitiesLocationData[location.id]}
                        />
                      ))}
                      
                    </div>
                  </div>
                </div>
              )}
            </div>
            {/* <OutageAlert show={showOutageAlert} onDismiss={handleDismiss} /> */}
          </div>
        );
      }}
    </LocationsController>
  );
}